import { Button } from "@/components/ui/button";
import { Menu, LogOut } from "lucide-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LanguageSwitcher from "./LanguageSwitcher";
import { WaitlistDialog } from "./WaitlistDialog";
import { supabase } from "@/integrations/supabase/client";
import friendatoLogo from "@/assets/friendato-logo.png";

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isComingSoonPage = location.pathname === "/";

  useEffect(() => {
    // Set up auth state listener FIRST (critical order)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Nav auth state changed:', event, session?.user?.email);
      setUser(session?.user ?? null);
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };
  
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Account for fixed navbar
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMenuOpen(false);
    }
  };
  
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    if (location.pathname === '/home') {
      e.preventDefault();
      scrollToSection(sectionId);
    }
  };

  return (
    <nav className="fixed top-0 w-full z-50 bg-background/95 backdrop-blur-lg border-b border-border shadow-sm">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 md:gap-3 hover:opacity-80 transition-opacity">
            <img 
              src={friendatoLogo} 
              alt="Friendato" 
              className="w-9 h-9 md:w-11 md:h-11 rounded-full object-cover"
            />
            <span className="text-lg md:text-xl lg:text-2xl font-bold bg-gradient-accent bg-clip-text text-transparent">
              Friendato
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            <a 
              href="/home#how-it-works" 
              onClick={(e) => handleNavClick(e, 'how-it-works')}
              className="text-sm lg:text-base text-foreground hover:text-primary transition-colors font-medium"
            >
              {t('nav.howItWorks')}
            </a>
            <a 
              href="/home#features" 
              onClick={(e) => handleNavClick(e, 'features')}
              className="text-sm lg:text-base text-foreground hover:text-primary transition-colors font-medium"
            >
              {t('nav.features')}
            </a>
            <Link to="/events" className="text-sm lg:text-base text-foreground hover:text-primary transition-colors font-medium">
              {t('nav.events')}
            </Link>
            <Link to="/success-stories" className="text-sm lg:text-base text-foreground hover:text-primary transition-colors font-medium">
              {t('nav.successStories')}
            </Link>
            <Link to="/careers" className="text-sm lg:text-base text-foreground hover:text-primary transition-colors font-medium">
              {t('nav.careers')}
            </Link>
            <Link to="/blog" className="text-sm lg:text-base text-foreground hover:text-primary transition-colors font-medium">
              {t('nav.blog')}
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3 lg:gap-4">
            <LanguageSwitcher />
            {user ? (
              <Button variant="outline" size="sm" className="min-h-[44px] gap-2" onClick={handleSignOut}>
                <LogOut className="w-4 h-4" />
                {t('nav.signOut')}
              </Button>
            ) : isComingSoonPage ? (
              <WaitlistDialog trigger={
                <Button size="sm" className="min-h-[44px] bg-gradient-accent hover:opacity-90">
                  {t('waitlist.joinWaitlist')}
                </Button>
              } />
            ) : (
              <>
                <Button variant="ghost" size="sm" className="min-h-[44px]" onClick={() => navigate('/matches')}>{t('nav.signIn')}</Button>
                <Button variant="hero" size="sm" className="min-h-[44px]" onClick={() => navigate('/matches')}>{t('nav.getStarted')}</Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <Menu className="w-6 h-6 text-foreground" />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 space-y-1 border-t border-border animate-fade-in">
            <a 
              href="/home#how-it-works" 
              onClick={(e) => handleNavClick(e, 'how-it-works')}
              className="block px-3 py-3 text-base text-foreground hover:bg-muted hover:text-primary transition-colors font-medium rounded-lg touch-manipulation"
            >
              {t('nav.howItWorks')}
            </a>
            <a 
              href="/home#features" 
              onClick={(e) => handleNavClick(e, 'features')}
              className="block px-3 py-3 text-base text-foreground hover:bg-muted hover:text-primary transition-colors font-medium rounded-lg touch-manipulation"
            >
              {t('nav.features')}
            </a>
            <Link to="/events" className="block px-3 py-3 text-base text-foreground hover:bg-muted hover:text-primary transition-colors font-medium rounded-lg touch-manipulation">
              {t('nav.events')}
            </Link>
            <Link to="/success-stories" className="block px-3 py-3 text-base text-foreground hover:bg-muted hover:text-primary transition-colors font-medium rounded-lg touch-manipulation">
              {t('nav.successStories')}
            </Link>
            <Link to="/careers" className="block px-3 py-3 text-base text-foreground hover:bg-muted hover:text-primary transition-colors font-medium rounded-lg touch-manipulation">
              {t('nav.careers')}
            </Link>
            <Link to="/blog" className="block px-3 py-3 text-base text-foreground hover:bg-muted hover:text-primary transition-colors font-medium rounded-lg touch-manipulation">
              {t('nav.blog')}
            </Link>
            <div className="flex flex-col gap-3 pt-4 border-t border-border mt-2">
              <LanguageSwitcher />
              {user ? (
                <Button variant="outline" size="lg" className="w-full min-h-[48px] touch-manipulation gap-2" onClick={handleSignOut}>
                  <LogOut className="w-4 h-4" />
                  {t('nav.signOut')}
                </Button>
              ) : isComingSoonPage ? (
                <WaitlistDialog trigger={
                  <Button size="lg" className="w-full min-h-[48px] touch-manipulation bg-gradient-accent hover:opacity-90">
                    {t('waitlist.joinWaitlist')}
                  </Button>
                } />
              ) : (
                <>
                  <Button variant="ghost" size="lg" className="w-full min-h-[48px] touch-manipulation" onClick={() => navigate('/matches')}>{t('nav.signIn')}</Button>
                  <Button variant="hero" size="lg" className="w-full min-h-[48px] touch-manipulation" onClick={() => navigate('/matches')}>{t('nav.getStarted')}</Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
